// Reusable sets of dropdown options ("presets"). A preset is saved from the
// options a Dropdown question currently holds and can be applied to any other
// Dropdown question on the same form, so a list like Pass / Fail / N/A only has
// to be typed once.
//
// Presets live inside the form schema, which keeps them versioned with the form
// and needs no extra storage. Yes/No ships with every form; deleting it hides
// it for that form only (recorded in `hiddenPresetIds`), so other forms — and
// new ones — still start with it.

export const BUILT_IN_PRESETS = [
  { id: 'builtin_yes_no', name: 'Yes/No', options: ['Yes', 'No'], builtIn: true },
];

export const makePresetId = () => `preset_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

// Drop blanks and surrounding whitespace: half-typed options shouldn't be
// baked into a saved preset.
export const cleanPresetOptions = (options) =>
  (Array.isArray(options) ? options : [])
    .map((option) => (typeof option === 'string' ? option.trim() : ''))
    .filter(Boolean);

const customPresets = (schema) =>
  (Array.isArray(schema?.optionPresets) ? schema.optionPresets : []).filter(
    (preset) => preset && typeof preset.id === 'string' && Array.isArray(preset.options),
  );

const hiddenIds = (schema) =>
  (Array.isArray(schema?.hiddenPresetIds) ? schema.hiddenPresetIds : []).filter(
    (id) => typeof id === 'string',
  );

// Every preset offered for this form: the built-ins the author hasn't removed,
// followed by the ones they saved themselves.
export const listOptionPresets = (schema) => {
  const hidden = new Set(hiddenIds(schema));
  return [
    ...BUILT_IN_PRESETS.filter((preset) => !hidden.has(preset.id)),
    ...customPresets(schema),
  ];
};

export const findPresetByName = (schema, name) => {
  const target = (name || '').trim().toLowerCase();
  if (!target) return null;
  return listOptionPresets(schema).find((preset) => preset.name.toLowerCase() === target) || null;
};

// Save (or overwrite) a named preset. Overwriting matches on name so re-saving
// "Pass/Fail" updates the existing row instead of stacking duplicates.
export const withSavedPreset = (schema, name, options) => {
  const trimmedName = (name || '').trim();
  const cleaned = cleanPresetOptions(options);
  if (!trimmedName || cleaned.length === 0) return schema;

  const existing = customPresets(schema);
  const matchIndex = existing.findIndex(
    (preset) => preset.name.trim().toLowerCase() === trimmedName.toLowerCase(),
  );
  const saved = {
    id: matchIndex === -1 ? makePresetId() : existing[matchIndex].id,
    name: trimmedName,
    options: cleaned,
  };
  const optionPresets =
    matchIndex === -1
      ? [...existing, saved]
      : existing.map((preset, index) => (index === matchIndex ? saved : preset));

  return { ...schema, optionPresets };
};

// Remove a preset from this form. Built-ins can't be deleted outright, so they
// are recorded as hidden instead.
export const withoutPreset = (schema, presetId) => {
  const isBuiltIn = BUILT_IN_PRESETS.some((preset) => preset.id === presetId);
  if (isBuiltIn) {
    const hidden = hiddenIds(schema);
    if (hidden.includes(presetId)) return schema;
    return { ...schema, hiddenPresetIds: [...hidden, presetId] };
  }
  return {
    ...schema,
    optionPresets: customPresets(schema).filter((preset) => preset.id !== presetId),
  };
};
